import type { ThreadMessage } from "@assistant-ui/react";
import type { AgentConfig, Lead, LeadChange } from "../leads/types";
import { SERVER_SKU_PROMPT } from "../leads/hardware";
import { createEmptyLead } from "../leads/utils";
import {
  directionLabels,
  formatPrice,
  formatQuantity,
  getFreshness,
} from "../leads/utils";
import { normalizeParsedLead, parseLocally } from "./parser";
import { prepareImageForModel } from "./imagePreprocessing";
import type { AgentAction, AgentResult, ChangeReceipt } from "./types";

const STATUSES: Lead["status"][] = [
  "draft",
  "active",
  "locked",
  "closed",
  "done",
  "archived",
];

export type AgentMode = "chat" | "work";

const SYSTEM_PROMPT = `你是大宗物品交易团队的盘口 Agent。根据对话和当前盘口，回答业务问题或实际整理数据。
只输出 JSON，不要 Markdown，格式：{"reasoning":"给操作者看的简要分析过程，可为空","reply":"简洁中文回答","actions":[{"type":"create","lead":{...}},{"type":"update","leadId":"已有盘口ID","changes":{...}},{"type":"delete","leadId":"已有盘口ID"}],"pending":["仍待确认的信息"]}。
没有数据变更时 actions=[]。不要臆造价格、数量、联系方式或交易条件。
lead 和 changes 可用字段：direction(sell/buy), status(draft/active/locked/closed/done/archived), category, brand, sku, model, configuration, memory_capacity, quantity(number|null), unit, price(number|null), currency(CNY/USD/USDT), estimated_fee_per_unit(number|null), price_basis, availability(stock/transit/presale/unknown), location, available_at(ISO|null), valid_until(ISO), payment_terms, inspection, transaction_steps(string[]), company, contact_name, contact_info, source_channel, raw_text, notes, ai_confidence(0到1)。
价格字段必须是明确的单价；原文只有总价或口径不清时 price=null，并把原文写进 price_basis 和 pending。预估居间费与报价使用同一币种。
B300/H200 是 GPU 平台，不是服务器 SKU。model 只写平台与内存版本（如 B300 3T）；sku 只能填写原文明确出现的官方服务器型号，未出现则为空。不要把 8 卡、8 GPU、整机、HGX/DGX 或固定机箱参数重复写入 model/configuration；configuration 只保留风冷、液冷等交易变量。官方目录：${SERVER_SKU_PROMPT}。
transaction_steps 按实际先后拆成有序步骤，例如 ["验资（飘 1.2 USDT）","现场验货","付款（验一付一）"]；不要把多个步骤塞进一个字符串。
“已卖／已售／没货”应把对应旧盘口 status=closed，不要标记为 done；“只剩新型号”应另建 draft 盘口，旧原文继续保留。
现货默认 48 小时有效，求购默认 72 小时。相对时间基于当前时间。update 只能使用给出的真实 leadId。
图片中的文字也要读取；无法看清的项目放入 pending。
长聊天截图会按原始顺序切成多段，段首标注“长截图第 N 段”。必须逐段全部读取后再合并，通常越靠下时间越新，不能只读开头或结尾。
同一地区、型号、内存版本和买卖方向的后续更新覆盖旧值；不同地区、内存版本或买卖方向必须保留为不同盘口，不得合并。
库存数量优先级最高的是卖方后续明确说出的“剩／剩余／还有 X 台”。例如同一香港 B300 3T 讨论中，卖方先说“总量 256 台”，后说“我刚卖了一些，剩 120 台”，最终 quantity 必须是 120，绝不能保留 256，也不能把剩余数量列为待确认；除非该句明确指出是另一批货。
图片里明确写出的数量、单价、币种、地点和交易步骤必须填入对应字段，不得仅因历史消息出现其他数字就把最新明确值丢入 pending。只有真正缺失、看不清或口径矛盾的字段才放入 pending。`;

function stripJsonFence(content: string): string {
  return content
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "");
}

function validStatus(value: unknown, fallback: Lead["status"]): Lead["status"] {
  return STATUSES.includes(value as Lead["status"])
    ? (value as Lead["status"])
    : fallback;
}

function localSpecialUpdate(
  text: string,
  leads: Lead[],
  owner: string,
): AgentResult | null {
  if (
    !/已卖|已售|賣了|卖了|只剩|只剩下/.test(text) ||
    !/4\s*T/i.test(text) ||
    !/3\s*T/i.test(text)
  )
    return null;

  const original = createEmptyLead("sell", owner);
  original.status = "closed";
  original.brand = "技嘉";
  original.sku = "B300";
  original.model = "B300 4T";
  original.configuration = "4T 版本";
  original.memory_capacity = "4T";
  original.quantity = 128;
  original.currency = "USDT";
  original.price = null;
  original.price_basis = "原文：230 萬 USDT（總價／單價待確認）";
  original.location = "馬來西亞 · 槟城";
  original.payment_terms = "驗資（飄 1.2 USDT）；現場驗貨付款（驗一付一）";
  original.inspection = "現場驗貨，驗一付一";
  original.transaction_steps = [
    "验资（飘 1.2 USDT）",
    "现场验货",
    "付款（验一付一）",
  ];
  original.contact_name = "A 货主";
  original.source_channel = "Agent 对话";
  original.raw_text = text;
  original.notes = "周六反馈原 4T 已售，关闭旧盘口；保留周五原始信息。";

  const replacement = createEmptyLead("sell", owner);
  replacement.status = "draft";
  replacement.brand = "技嘉";
  replacement.sku = "B300";
  replacement.model = "B300 3T";
  replacement.configuration = "3T 版本";
  replacement.memory_capacity = "3T";
  replacement.quantity = null;
  replacement.price = null;
  replacement.currency = "USDT";
  replacement.price_basis = "价格待确认";
  replacement.location = "馬來西亞 · 槟城";
  replacement.contact_name = "A 货主";
  replacement.source_channel = "Agent 对话";
  replacement.raw_text = text;
  replacement.notes =
    "由原 4T 已售的跟进信息新建；3T 数量、价格和交付时间待确认。";

  const sameParty = (lead: Lead) =>
    lead.contact_name.replace(/\s/g, "") === "A货主";
  const existing4T = leads.find(
    (lead) =>
      sameParty(lead) &&
      /4\s*T/i.test(`${lead.model} ${lead.configuration}`) &&
      lead.status !== "closed",
  );
  const existing3T = leads.find(
    (lead) =>
      sameParty(lead) &&
      /3\s*T/i.test(`${lead.model} ${lead.configuration}`) &&
      !["closed", "done"].includes(lead.status),
  );
  const actions: AgentAction[] = [
    existing4T
      ? {
          type: "update",
          leadId: existing4T.id,
          changes: {
            status: "closed",
            notes:
              `${existing4T.notes}\n周六反馈已售；跟进原文：${text}`.trim(),
          },
        }
      : {
          type: "create",
          lead: original as unknown as Record<string, unknown>,
        },
  ];
  if (!existing3T)
    actions.push({
      type: "create",
      lead: replacement as unknown as Record<string, unknown>,
    });

  return {
    reply: "已按“旧盘关闭、新盘另建”处理这次版本变化。",
    actions,
    pending: ["3T 的数量、价格和交付时间", "原 230 萬 USDT 是总价还是单价"],
  };
}

export function runLocalAgent(
  text: string,
  leads: Lead[],
  owner: string,
  hasImages = false,
  mode: AgentMode = "work",
): AgentResult {
  if (hasImages) {
    return {
      reply:
        "已收到图片和文字，但本地规则不能读取图片内容，因此没有添加或修改盘口。请在 Agent 设置中连接支持视觉的模型后重新发送。",
      actions: [],
      pending: ["图片及随附文字尚未解析"],
    };
  }

  const special =
    mode === "work" ? localSpecialUpdate(text, leads, owner) : null;
  if (special) return special;

  if (
    /出售|求购|采购|现货|有货|供应|貨主|货主|價格|价格|USDT|\bB300\b|\bH100\b|\bH200\b/i.test(
      text,
    )
  ) {
    const drafts = parseLocally(text, owner);
    return {
      reply:
        mode === "chat"
          ? `对话模式识别到 ${drafts.length} 条买卖信息，仅供讨论，没有写入盘口。`
          : `已识别 ${drafts.length} 条买卖信息并写入盘口。`,
      actions:
        mode === "chat"
          ? []
          : drafts.map((lead) => ({
              type: "create",
              lead: lead as unknown as Record<string, unknown>,
            })),
      pending: ["请核对型号、价格口径、数量和交割条件"],
    };
  }

  const current = leads.filter((lead) =>
    ["fresh", "expiring"].includes(getFreshness(lead)),
  );
  const sell = current.filter((lead) => lead.direction === "sell").length;
  const buy = current.filter((lead) => lead.direction === "buy").length;
  const expiring = current.filter(
    (lead) => getFreshness(lead) === "expiring",
  ).length;
  return {
    reply: `当前有 ${sell} 条有效出售、${buy} 条有效求购，${expiring} 条需要重新核验。你可以直接粘贴新消息，或告诉我“把某条盘口关闭”。`,
    actions: [],
    pending: [],
  };
}

type ModelMessagePart =
  | { type: "text"; text: string }
  | { type: "image_url"; image_url: { url: string } };

async function appendImage(parts: ModelMessagePart[], source: string) {
  const images = await prepareImageForModel(source);
  if (images.length > 1) {
    parts.push({
      type: "text",
      text: `下面是一张超长聊天截图，已按原始从上到下顺序切成 ${images.length} 段；请全部读取后按时间线合并。`,
    });
  }
  for (const image of images) {
    parts.push({ type: "image_url", image_url: { url: image } });
  }
}

async function messageForModel(
  message: ThreadMessage,
): Promise<Record<string, unknown> | null> {
  if (message.role === "system") return null;
  const parts: ModelMessagePart[] = [];
  for (const part of message.content) {
    if (part.type === "text") parts.push({ type: "text", text: part.text });
    else if (message.role === "user" && part.type === "image") {
      await appendImage(parts, part.image);
    }
  }
  if (message.role === "user") {
    for (const attachment of message.attachments ?? []) {
      for (const part of attachment.content) {
        if (part.type === "image") await appendImage(parts, part.image);
        else if (part.type === "text")
          parts.push({ type: "text", text: part.text });
      }
    }
  }
  if (!parts.length) return null;
  if (parts.every((part) => part.type === "text")) {
    return {
      role: message.role,
      content: parts.map((part) => part.text).join("\n"),
    };
  }
  return { role: message.role, content: parts };
}

export async function runRemoteAgent(
  messages: readonly ThreadMessage[],
  leads: Lead[],
  owner: string,
  config: AgentConfig,
  abortSignal: AbortSignal,
  mode: AgentMode = "work",
): Promise<AgentResult> {
  const endpoint = `${config.baseUrl.replace(/\/$/, "")}/chat/completions`;
  const controller = new AbortController();
  const abort = () => controller.abort();
  abortSignal.addEventListener("abort", abort, { once: true });
  const timer = window.setTimeout(() => controller.abort(), 120_000);
  const leadContext = leads.slice(0, 100).map((lead) => ({
    id: lead.id,
    direction: lead.direction,
    status: lead.status,
    brand: lead.brand,
    sku: lead.sku,
    model: lead.model,
    configuration: lead.configuration,
    memory_capacity: lead.memory_capacity,
    quantity: lead.quantity,
    unit: lead.unit,
    price: lead.price,
    currency: lead.currency,
    estimated_fee_per_unit: lead.estimated_fee_per_unit,
    transaction_steps: lead.transaction_steps,
    location: lead.location,
    company: lead.company,
    contact_name: lead.contact_name,
    valid_until: lead.valid_until,
    updated_at: lead.updated_at,
  }));
  try {
    const modelMessages = (
      await Promise.all(messages.map(messageForModel))
    ).filter((message): message is Record<string, unknown> => Boolean(message));
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: config.model,
        temperature: 0.1,
        response_format: { type: "json_object" },
        messages: [
          {
            role: "system",
            content:
              mode === "chat"
                ? `${SYSTEM_PROMPT}\n当前是对话模式：只分析、回答和讨论，严禁返回任何 create、update 或 delete 操作，actions 必须始终为 []。`
                : `${SYSTEM_PROMPT}\n当前是工作模式：用户要求整理或变更盘口时，返回必要且准确的操作。`,
          },
          {
            role: "user",
            content: `当前时间：${new Date().toISOString()}\n默认负责人：${owner}\n当前盘口：${JSON.stringify(leadContext)}`,
          },
          ...modelMessages,
        ],
      }),
      signal: controller.signal,
    });
    if (!response.ok)
      throw new Error(
        `接口返回 ${response.status}：${(await response.text()).slice(0, 240)}`,
      );
    const payload = await response.json();
    const content = payload?.choices?.[0]?.message?.content;
    if (typeof content !== "string")
      throw new Error("接口未返回可解析的消息内容");
    const parsed = JSON.parse(stripJsonFence(content)) as Partial<AgentResult>;
    return {
      reply: String(parsed.reply ?? "已处理。"),
      reasoning:
        typeof parsed.reasoning === "string" ? parsed.reasoning.trim() : "",
      actions: Array.isArray(parsed.actions) ? parsed.actions : [],
      pending: Array.isArray(parsed.pending) ? parsed.pending.map(String) : [],
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw new Error("Agent 响应超时或已停止，请重试");
    if (error instanceof SyntaxError)
      throw new Error("模型返回了非标准 JSON，请重试");
    throw error;
  } finally {
    window.clearTimeout(timer);
    abortSignal.removeEventListener("abort", abort);
  }
}

function describeChange(change: LeadChange): string {
  const lead = change.after ?? change.before!;
  const action =
    change.type === "create"
      ? "新增"
      : change.type === "delete"
        ? "删除"
        : change.after?.status === "closed" &&
            change.before?.status !== "closed"
          ? "关闭"
          : "修改";
  const state = change.after
    ? directionLabels[change.after.direction]
    : directionLabels[lead.direction];
  return `- **${action}**｜${state} ${lead.brand ? `${lead.brand} ` : ""}${lead.model}｜${formatQuantity(lead)}｜${formatPrice(lead)}｜${lead.location || "交割地待确认"}`;
}

export function buildChangeSummary(receipt: ChangeReceipt): string {
  const pending = receipt.pending.length
    ? `\n\n**仍待确认**\n${receipt.pending.map((item) => `- ${item}`).join("\n")}`
    : "";
  return `已完成并保存 ${receipt.changes.length} 条盘口变更：\n\n${receipt.changes.map(describeChange).join("\n")}${pending}\n\n**结果已保存。** 未点 Confirm 也默认保留；需要时点 Undo 可恢复本次变更。`;
}

export async function executeAgentResult(
  result: AgentResult,
  leads: Lead[],
  apply: (changes: LeadChange[]) => Promise<void>,
  owner: string,
  mode: AgentMode = "work",
): Promise<{ receipt: ChangeReceipt | null; text: string; reasoning: string }> {
  const pending = [...result.pending];
  const reasoning = result.reasoning?.trim() ?? "";
  if (mode === "chat") {
    const pendingText = pending.length
      ? `\n\n**仍待确认**\n${pending.map((item) => `- ${item}`).join("\n")}`
      : "";
    return {
      receipt: null,
      reasoning,
      text: `${result.reply}\n\n**对话模式：未修改任何盘口数据。**${pendingText}`,
    };
  }

  const changes: LeadChange[] = [];

  for (const action of result.actions) {
    if (action.type === "create" && action.lead) {
      const raw = action.lead;
      const after = normalizeParsedLead(raw, String(raw.raw_text ?? ""), owner);
      after.status = validStatus(raw.status, after.status);
      changes.push({ type: "create", before: null, after });
      continue;
    }

    const target = leads.find((lead) => lead.id === action.leadId);
    if (!target) {
      pending.push(
        `未找到要${action.type === "delete" ? "删除" : "修改"}的盘口 ${action.leadId ?? "（缺少 ID）"}，已跳过`,
      );
      continue;
    }
    if (action.type === "delete") {
      changes.push({ type: "delete", before: target, after: null });
      continue;
    }
    if (action.type === "update" && action.changes) {
      const raw = { ...target, ...action.changes } as Record<string, unknown>;
      const normalized = normalizeParsedLead(
        raw,
        String(raw.raw_text ?? target.raw_text),
        target.owner,
      );
      const after: Lead = {
        ...normalized,
        id: target.id,
        created_at: target.created_at,
        created_by: target.created_by,
        status: validStatus(action.changes.status, target.status),
      };
      changes.push({ type: "update", before: target, after });
    }
  }

  if (!changes.length) {
    const pendingText = pending.length
      ? `\n\n**仍待确认**\n${pending.map((item) => `- ${item}`).join("\n")}`
      : "";
    return {
      receipt: null,
      reasoning,
      text: `${result.reply}\n\n**未修改任何盘口数据。**${pendingText}`,
    };
  }

  await apply(changes);
  const receipt: ChangeReceipt = {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    changes,
    pending,
  };
  return { receipt, reasoning, text: buildChangeSummary(receipt) };
}
